import { useEffect, useState } from "react";
import { FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import BgImg from "../Images/Bg2.png";
import Logo from "../Images/Cert-Logo.png";
import Bg from "../Images/background1.png";
import { toast } from "react-toastify";
import {
  useGenerateOtpMutation,
  useLoginMutation,
} from "../Redux/auth/authApis";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [generateOtp, { isLoading: isGeneratingOtp }] =
    useGenerateOtpMutation();
  const [login, { isLoading: isLoggingIn }] = useLoginMutation();

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleGenerateOtp = async (e) => {
    e.preventDefault();

    if (!email || !email.trim()) {
      toast.error("Please enter your email address", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    try {
      await generateOtp({ emailAddress: email }).unwrap();

      setOtpSent(true);
      setResendTimer(30);

      toast.success("OTP has been sent to your registered email address", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to send OTP. Please try again.",
        {
          position: "top-center",
          autoClose: 3000,
        },
      );
    }
  };

  const handleResendOtp = async () => {
    try {
      await generateOtp({ emailAddress: email }).unwrap();

      setResendTimer(30);

      toast.success("OTP has been resent to your email", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(error?.data?.message || "Failed to resend OTP", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!otp || !otp.trim()) {
      toast.error("Please enter the OTP", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    try {
      const response = await login({ emailAddress: email, otp }).unwrap();

      toast.success(response.message || "Login successful!", {
        position: "top-center",
        autoClose: 2000,
      });

      setTimeout(() => {
        response?.user?.role === "Admin"
          ? navigate("/admin")
          : navigate("/home");
      }, 1000);
    } catch (error) {
      toast.error(error?.data?.message || "Invalid OTP. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-[#F5F5F5]">
      {/* Soft Warm Glow */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-[#FDF6EC]/40 via-transparent to-[#FAF3E8]/40"></div>

      {/* Optional Glow Blobs (very subtle) */}
      <div className="absolute -top-20 -left-20 w-[350px] h-[350px] bg-[#F7EBDD]/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-[350px] h-[350px] bg-[#F3E4D7]/40 rounded-full blur-3xl"></div>

      <div className="relative flex justify-center items-center h-screen px-4 z-10">
        <div className="flex flex-col md:flex-row shadow-xl  border border-gray-300 rounded-3xl w-full max-w-4xl">
          <div
            className="hidden md:block w-1/2 h-[500px] bg-cover bg-indigo-50/50 bg-no-repeat rounded-l-3xl"
            style={{ backgroundImage: `url(${BgImg})` }}
          >
            <div className="flex flex-col justify-start  mt-6 h-full px-4">
              <h1
                className="text-white text-xl md:text-2xl font-extrabold mb-2 text-center drop-shadow-lg "
                style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.6)" }}
              >
                UNLOCK YOUR BUSINESS POTENTIAL
              </h1>
              <div className="flex items-center gap-2 ml-10 w-full max-w-xs drop-shadow-md">
                <div className="flex-1 h-[1px] bg-white/60"></div>
                <h2 className="text-white text-sm md:text-base font-medium text-center">
                  Simplify, Streamline, Succeed!
                </h2>
                <div className="flex-1 h-[1px] bg-white/60"></div>
              </div>
            </div>
          </div>

          <div className="w-full md:w-1/2 bg-indigo-50/50 rounded-lg md:rounded-l-none md:rounded-r-3xl p-6 sm:p-8">
            <div className="flex justify-center mb-4">
              <img
                src={Logo}
                alt="Logo"
                className="w-26 h-auto sm:w-38 md:w-44 "
              />
            </div>

            <h2 className="mb-6 text-center text-lg md:text-2xl font-semibold text-gray-800 tracking-wide">
              Admin Login
            </h2>

            <form onSubmit={otpSent ? handleLogin : handleGenerateOtp}>
              <div className="mb-4 mt-2">
                <label className="block text-md font-medium  text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-900 text-lg pointer-events-none" />
                  <input
                    type="email"
                    className="w-full h-12 pl-12 pr-4 border rounded-xl border-gray-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-600 transition placeholder-gray-600 shadow-sm"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={otpSent || isGeneratingOtp}
                    required
                  />
                </div>
              </div>

              {otpSent && (
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-md font-medium text-gray-700">
                      OTP
                    </label>
                    {resendTimer > 0 ? (
                      <p className="text-xs text-gray-500">
                        Resend in {resendTimer}s
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        className="text-xs text-blue-800 hover:text-blue-900 font-medium"
                        disabled={isGeneratingOtp}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    className="w-full h-12 px-4  border rounded-xl border-gray-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-600 placeholder-gray-600 transition"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    maxLength={6}
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={isGeneratingOtp || isLoggingIn}
                className={`w-full h-12 mt-4 font-bold rounded-lg transition duration-300 ${
                  isGeneratingOtp || isLoggingIn
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-blue-800 to-blue-900  text-white"
                }`}
              >
                {isGeneratingOtp
                  ? "Sending OTP..."
                  : isLoggingIn
                    ? "Logging in..."
                    : otpSent
                      ? "Login"
                      : "Generate OTP"}
              </button>

              {otpSent && (
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setOtpSent(false);
                      setOtp("");
                      setResendTimer(0);
                    }}
                    className="text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    Change Email Address
                  </button>
                </div>
              )}
            </form>

            <div className="mt-2 text-center">
              <p className="text-xs text-gray-500">
                OTP is valid for 2 minutes
              </p>
              <p className="mt-6 text-center text-sm text-gray-800">
                Don't have an account?{" "}
                <span
                  className="text-blue-700 font-semibold hover:text-blue-800 cursor-pointer"
                  onClick={() => navigate("/register")}
                >
                  Register
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
